"use client";

import { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { apiGet, fetchFees } from "../services/api";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronLeft,
  ChevronRight,
  Download,
  RefreshCw,
  Printer,
} from "lucide-react";
import Footer from "./Footer";
import NotificationPopup from "./NotificationPopup";

export default function HostelForms({ onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [feesData, setFeesData] = useState(null);
  const [feesLoading, setFeesLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [notification, setNotification] = useState(null);
  const totalPages = 6;
  const token = localStorage.getItem("token");

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const res = await apiGet("student");
        if (res.error) {
          setError(res.error);
        } else {
          setStudentData(res);
          await loadFees();

          setFormData({
            studentName: res.student.name || "",
            enrollmentNo: res.student.enrollment_id || "",
            rollNo: res.student.roll_number || "",
            studentContact: res.student.mobile || "",
            fatherName: res.profile?.FatherName || "",
            fatherContact: res.profile?.Fmobile || "",
            aadharNo: res.profile?.aadhar_number || "",
            address: res.profile?.CAddress || "",
            age: res.profile?.age || "",
            dateOfBirth: res.profile?.CDob || "",
            gender: res.profile?.gender || "Male",
            programme: res.profile?.ProgrammeName || "",
            motherName: res.profile?.MotherName || "",
            motherPhone: res.profile?.mother_mobile || "",
            height: res.profile?.height || "",
            weight: res.profile?.weight || "",
            bloodGroup: res.profile?.blood_group || "",
            email: res.student.email || "",
      

            // CHANGE THESE TWO LINES TO THIS:
            hostel: res.allocation?.hostel_name || "",
            room: res.allocation?.room_number || "",
             capacity:res.allocation?.capacity || "",


            // Editable fields can stay as they are
            srNo: "",
            feesAmount: "",
            studentDeclarationDate: "",
            parentDeclarationDate: "",
            otherContactName: "",
            otherContactPhone: "",
            localGuardianName: "",
            localGuardianContact: "",
            localGuardianAddress: "",
            fatherOccupation: "",
          });
        }
      } catch (err) {
        setError("Unable to fetch student data.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  async function loadFees() {
    setFeesLoading(true);
    try {
      const data = await fetchFees();
      if (data.error) {
        console.error("Fees error:", data.error);
      } else {
        setFeesData(data);
      }
    } catch (err) {
      console.error("Unable to fetch fees data.");
    } finally {
      setFeesLoading(false);
    }
  }

  const getImageUrl = () => {
    if (!studentData) return "";
    const candidateId = studentData.profile?.CandidateId;
    const photograph = studentData.profile?.photograph;
    if (candidateId) {
      return `https://pravesh.gbu.ac.in/app/userimages/${candidateId}.jpg`;
    }
    if (photograph) {
      const cleanedPath = photograph
        .replace(/^(\.\.\/)+/, "")
        .replace(/^\/+/, "");
      return `https://hostels.gbu.ac.in/ohms/${cleanedPath}`;
    }
    return "";
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const downloadPDF = async (pageNumber) => {
    setDownloading(true);
    try {
      // Import libraries dynamically
      const jsPDF = (await import("jspdf")).default;
      const html2canvas = (await import("html2canvas")).default;

      const element = document.getElementById(`form-page-${pageNumber}`);
      if (!element) {
        throw new Error(`Form page ${pageNumber} not found`);
      }

      // Create canvas with better settings for A4
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        width: 794, // A4 width in pixels at 96 DPI
        height: 1123, // A4 height in pixels at 96 DPI
        scrollX: 0,
        scrollY: 0,
        logging: false,
        letterRendering: true,
      });

      // Create PDF with exact A4 dimensions
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = 297; // A4 height in mm

      // Add image to PDF with exact A4 dimensions
      pdf.addImage(
        canvas.toDataURL("image/jpeg", 0.98),
        "JPEG",
        0,
        0,
        imgWidth,
        imgHeight
      );

      // Save PDF
      pdf.save(
        `Hostel_Form_Page_${pageNumber}_${formData.rollNo || "Student"}.pdf`
      );
      setNotification({
        type: "success",
        message: `Page ${pageNumber} downloaded successfully!`,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      setNotification({
        type: "error",
        message: `Error generating PDF for page ${pageNumber}. Please try again.`,
      });
    } finally {
      setDownloading(false);
    }
  };
  
 // Download all pages as single PDF
  const downloadAllPDFs = async () => {
    setDownloading(true);
    try {
      const jsPDF = (await import("jspdf")).default;
      const html2canvas = (await import("html2canvas")).default;

      // Create a single PDF with all pages
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      let pagesAdded = 0;
      // Fixed matching order array to strictly correspond to the 5 view states rendered sequentially
      const pageIds = ["form-page-1", "form-page-2", "form-page-5", "form-page-4","form-page-6", "form-page-3"];

      // Save initial page state to restore it cleanly at the end
      const originalPage = currentPage;

      // Download each page and add to PDF
      for (let pageIndex = 0; pageIndex < pageIds.length; pageIndex++) {
        try {
          const targetPageNum = pageIndex + 1;
          
          // Force state update to append the virtual layout explicitly into active layout view
          setCurrentPage(targetPageNum);
          
          // Wait longer for React to completely commit the lifecycle updates and sync the network images
          await new Promise((resolve) => setTimeout(resolve, 1500));

          // Clear reference selector directly from fresh layout tree lookup
          const elementId = pageIds[pageIndex];
          let element = document.getElementById(elementId);
          
          if (!element) {
            console.warn(`Element ${elementId} not found, retrying...`);
            await new Promise((resolve) => setTimeout(resolve, 1000));
            element = document.getElementById(elementId);
            if (!element) {
              console.error(`Element ${elementId} missing completely from rendering layout tree.`);
              continue;
            }
          }
          
          // Capture the target viewport page view block node reference
          const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#ffffff",
            width: 794,
            height: 1123,
            scrollX: 0,
            scrollY: 0,
            logging: false,
            letterRendering: true,
          });

          // Append a fresh structural document framework partition for successive components
          if (pagesAdded > 0) {
            pdf.addPage("a4", "portrait");
          }

          const imgWidth = 210;
          const imgHeight = 297;

          pdf.addImage(
            canvas.toDataURL("image/jpeg", 0.95),
            "JPEG",
            0,
            0,
            imgWidth,
            imgHeight
          );

          pagesAdded++;
        } catch (pageError) {
          console.error(`Error on page execution index ${pageIndex + 1}:`, pageError);
        }
      }

      if (pagesAdded === 0) {
        throw new Error("No layout elements could be parsed successfully.");
      }

      pdf.save(`Hostel_Forms_All_Pages_${formData.rollNo || "Student"}.pdf`);
      setNotification({
        type: "success",
        message: `All ${pagesAdded} pages processed and downloaded into a single master document!`,
      });

      // Restore viewport safely back to the user's focus view state
      setCurrentPage(originalPage);
    } catch (error) {
      console.error("PDF compiling failure exception handler:", error);
      setNotification({
        type: "error",
        message: "Error processing the master package document. Please compile layout targets individually.",
      });
    } finally {
      setDownloading(false);
    }
  };

  const printPage = (pageNumber) => {
    const element = document.getElementById(`form-page-${pageNumber}`);
    if (!element) return;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Hostel Form Page ${pageNumber}</title>
          <style>
            @page { 
              size: A4; 
              margin: 0.5in; 
            }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 0;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            .no-print { display: none !important; }
            input, textarea, select { 
              border: 1px solid #333 !important; 
              background: white !important;
              -webkit-appearance: none;
            }
            .readonly-field {
              background-color: #f5f5f5 !important;
              border: 1px solid #ccc !important;
            }
          </style>
        </head>
        <body>
          ${element.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

/**
 * Change: added "Room Type" (Single/Double Seater) right after Room No.
 *
 * 1. ROOM ALLOCATION box: the grid changes from 2 columns / 2 rows
 *    (Hostel Name, Room No. / Mess No., Card No.) to 3 columns for the
 *    first row (Hostel Name, Room No., Room Type), keeping Mess No. and
 *    Card No. on the row below.
 * 2. HOSTEL COPY summary: added a "Room Type:" line right after
 *    "Room No. / Enrollment No." so the copy stays consistent with the
 *    allocation box above it.
 *
 * Assumes a new field `formData.roomType` (e.g. "Single Seater" /
 * "Double Seater") - add it to your fetchData() setFormData(...) call
 * the same way `hostel` and `room` are populated, e.g.:
 *   roomType: res.allocation?.room_type || "",
 *
 * Everything else in renderFormPage1 is unchanged.
 */

/**
 * Change: every fontSize value in this component has been reduced by 5%
 * (original * 0.95). No other styling, layout, or logic was touched.
 */

const renderFormPage1 = () => (
  <div
    id="form-page-1"
    className="bg-white mx-auto"
    style={{
      width: "210mm",
      height: "280mm",
      padding: "10mm",
      fontFamily: "Arial, sans-serif",
      fontSize: "8pt",
      lineHeight: "1",
      color: "#000",
      overflow: "hidden",
      boxSizing: "border-box",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "5mm",
        borderBottom: "1px solid #000",
        paddingBottom: "3mm",
      }}
    >
      <div style={{ marginRight: "4mm", display: "flex", alignItems: "center" }}>
        {!logoError ? (
          <div>
            <img
              /* Hand-coded path check: encoding spaces to safely match strict server path formats */
              src="https://hostels.gbu.ac.in/images/logo/GBU%20logo.png"
              alt="GBU Logo"
              style={{
                width: "12mm",
                height: "12mm",
                objectFit: "contain",
              }}
              /* Removed crossOrigin attribute to allow standard un-tainted rendering asset loading */
              onError={() => setLogoError(true)}
            />
          </div>

        ) : (
          /* The reliable fallback circle triggers cleanly if the image path returns a server error */
          <div
            style={{
              width: "12mm",
              height: "12mm",
              backgroundColor: "#1e40af",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: "bold",
              fontSize: "7.6pt",
            }}
          >
            GBU
          </div>


        )}

      </div>
      <div style={{ flex: 1, textAlign: "center" }}>
        <h1 style={{ fontSize: "10.45pt", fontWeight: "bold", margin: "0" }}>
          गौतम बुद्ध विश्वविद्यालय
        </h1>
        <h2 style={{ fontSize: "9.5pt", fontWeight: "600", margin: "1mm 0" }}>
          Gautam Buddha University
        </h2>
        <div style={{ marginTop: "2mm" }}>
          <span
            style={{
              color: "black",
              padding: "1mm 3mm",
              fontSize: "8.55pt",
              fontWeight: "bold",
            }}
          >
            FORM-A
          </span>
        </div>
      </div>
    </div>


    <div style={{ textAlign: "center", marginBottom: "5mm" }}>

      <h4
        style={{
          fontWeight: "500",
          fontSize: "12pt",
          margin: "2mm 0",
          color: "#1e40af",
        }}
      >
        Hostel Allotment Form (2026-27)
      </h4>
    </div>

    {/* Student Information Grid */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: "3mm",
        marginBottom: "3mm",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "3mm",
        }}
      >
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "9.5pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Sr. No.(For Office Use Only):
          </label>
          <input
            type="text"
            style={{
              width: "100%",
              marginTop: "2mm",
              padding: "1.5mm",
              border: "1px solid #333",
              fontSize: "10.45pt",
              backgroundColor: "#f5f5f5",
              fontWeight: "500",
              minHeight: "6mm",
              display: "flex",
              alignItems: "center",
            }}
            value={formData.srNo || ""}
            onChange={(e) => handleInputChange("srNo", e.target.value)}
          />
        </div>
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "9.5pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Student Name:
          </label>
          <div
            style={{
              width: "100%",
              padding: "1.5mm",
              borderBottom: "1px solid #000",
              fontSize: "10.45pt",
              backgroundColor: "#fff",
            }}
          >
            {formData.studentName}
          </div>
        </div>
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "9.5pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Roll No / Enrollment No:
          </label>
          <div
            style={{
              width: "100%",
              padding: "1.5mm",
              borderBottom: "1px solid #000",
              fontSize: "10.45pt",
              backgroundColor: "#fff",
            }}
          >
            {formData.rollNo} / {formData.enrollmentNo}
          </div>
        </div>
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "9.5pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Student Contact No.:
          </label>
          <div
            style={{
              width: "100%",
              padding: "1.5mm",
              borderBottom: "1px solid #000",
              fontSize: "10.45pt",
              backgroundColor: "#fff",
            }}
          >
            {formData.studentContact}
          </div>
        </div>
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "9.5pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Father/Guardian Name:
          </label>
          <div
            style={{
              width: "100%",
              padding: "1.5mm",
              borderBottom: "1px solid #000",
              fontSize: "10.45pt",
              backgroundColor: "#fff",
            }}
          >
            {formData.fatherName}
          </div>
        </div>
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "9.5pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Father/Guardian Contact No.:
          </label>
          <div
            style={{
              width: "100%",
              marginTop: "2mm",
              padding: "1.5mm",
              border: "1px solid #333",
              fontSize: "10.45pt",
              backgroundColor: "#f5f5f5",
              fontWeight: "500",
              minHeight: "6mm",
              display: "flex",
              alignItems: "center",
            }}
          >
            {formData.fatherContact}
          </div>
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "9.5pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Aadhar No.
          </label>
          <div
            style={{
              width: "100%",
              padding: "1.5mm",
              border: "1px solid #333",
              fontSize: "10.45pt",
              backgroundColor: "#f5f5f5",
              fontWeight: "500",
              minHeight: "6mm",
              display: "flex",
              alignItems: "center",
            }}
          >
            {formData.aadharNo}
          </div>
        </div>
      </div>

      {/* Photo Section */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          border: "1px solid #333",
          padding: "2mm",
          backgroundColor: "#fafafa",
        }}
      >
        <div
          style={{
            width: "25mm",
            height: "32mm",
            border: "1px dashed #666",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1mm",
            textAlign: "center",
            backgroundColor: "#fff",
          }}
        >

          <span
            style={{ fontSize: "5.7pt", color: "#666", lineHeight: "1.1" }}
          >
            Attach Recent
            <br />
            Passport Size
            <br />
            Photograph
            <br />
            Here*
          </span>

        </div>
        <span style={{ fontSize: "5.7pt", fontWeight: "500" }}>
          Dated: {new Date().toLocaleDateString()}
        </span>
      </div>
    </div>

    {/* Checklist */}
<div
  style={{
    marginBottom: "2mm",
    border: "1px solid #333",
    padding: "2mm",
    backgroundColor: "#f9f9f9",
  }}
>
  <h4
    style={{
      fontWeight: "bold",
      marginBottom: "1.5mm",
      fontSize: "8.5pt",
      textAlign: "center",
      textDecoration: "underline",
    }}
  >
    Checklist* (✓ if attached, ✗ if not attached)
  </h4>
  <div
    style={{
      display: "grid",
      gridAutoFlow: "column",
      gridTemplateRows: "repeat(5, auto)",
      gridTemplateColumns: "1fr 1fr",
      gap: "1mm 2.5mm",
      fontSize: "6.5pt",
    }}
  >
    {[
      "1. Hostel & Mess Fee Receipt Amount Paid:",
      "2. Two Photograph of Student",
      "3. Aadhar Card (Photocopy)",
      "4. Photograph of Student's Father , Mother & Local Guardian",
      "5. Hostel Accommodation Form-B",
      "6. One Photo ID Proof of Parent/Guardian",
      "7. Undertaking- General Rules & Regulations",
      "8. Medical & Fitness Undertaking",
      "9. Antiragging Affidavit",
    ].map((item, index) => (
      <div
        key={index}
        style={{ display: "flex", alignItems: "center", gap: "1.2mm" }}
      >
        <div
          style={{
            width: "2.8mm",
            height: "2.8mm",
            border: "1px solid #333",
            backgroundColor: "white",
            flexShrink: 0,
          }}
        ></div>
        <span>{item}</span>
      </div>
    ))}
  </div>
</div>

    {/* Fees Information */}
 {feesData && feesData.success && feesData.sessions.length > 0 && (
<div
  style={{
    marginBottom: "3mm",
    border: "1px solid #1e40af",
    padding: "2mm",
    backgroundColor: "#f0f4ff",
  }}
>
  <h4
    style={{
      fontWeight: "bold",
      fontSize: "10.45pt",
      marginBottom: "1mm",
      color: "#1e40af",
      textAlign: "center",
    }}
  >
    Fees Information (2026-2027)
  </h4>
  {feesData.sessions
    .filter(
      (session) =>
        session.session_name === "2026-2027" ||
        session.session_name === "2026-27"
    )
    .map((session, idx) => (
      <div
        key={idx}
        style={{
          fontSize: "7.6pt",
          marginBottom: "0.5mm",
          textAlign: "center",
        }}
      >
        <strong>{session.session_name}:</strong> Academic: ₹
        {session.academic_fees_paid}/{session.academic_fees_total}, Hostel: ₹
        {session.hostel_fees_paid}/{session.hostel_fees_total}, Mess: ₹
        {session.mess_fees_paid}/{session.mess_fees_total}
        {session.allowed_to_allot_hostel && (
          <span
            style={{
              color: "#16a34a",
              fontWeight: "bold",
              marginLeft: "1mm",
            }}
          >
            (Eligible for Hostel)
          </span>
        )}
      </div>
    ))}
</div>
)}

    {/* Room Allocation */}
    <div
      style={{
        border: "1px solid #000",
        padding: "3mm",
        marginBottom: "3mm",
        backgroundColor: "#fff",
      }}
    >
      <h4
        style={{
          fontWeight: "bold",
          textAlign: "center",
          marginBottom: "2mm",
          fontSize: "9.5pt",
          textDecoration: "underline",
        }}
      >
        ROOM ALLOCATION (for office use only)
      </h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "3mm",
        }}
      >
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "7.6pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Hostel Name:
          </label>
          <div
            style={{
              width: "100%",
              padding: "1.5mm",
              borderBottom: "1px solid #000",
              fontSize: "8.55pt",
              backgroundColor: "#fff",
            }}
          >
            {formData.hostel}
          </div>
        </div>
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "7.6pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Room No.:
          </label>
          <div
            style={{
              width: "100%",
              padding: "1.5mm",
              borderBottom: "1px solid #000",
              fontSize: "8.55pt",
              backgroundColor: "#fff",
            }}
          >
            {formData.room}
          </div>
        </div>
        {/* NEW: Room Type (Single/Double Seater), placed right after Room No. */}
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "7.6pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Room Type:
          </label>
          <div
            style={{
              width: "100%",
              padding: "1.5mm",
              borderBottom: "1px solid #000",
              fontSize: "8.55pt",
              backgroundColor: "#fff",
            }}
          >
            {formData.capacity}
          </div>
        </div>
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "7.6pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Mess No.:
          </label>
          <div
            style={{
              width: "100%",
              padding: "1.5mm",
              border: "1px solid #333",
              fontSize: "8.55pt",
              minHeight: "6mm",
              backgroundColor: "white",
            }}
          ></div>
        </div>
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "7.6pt",
              display: "block",
              marginBottom: "1.5mm",
            }}
          >
            Card No.:
          </label>
          <div
            style={{
              width: "100%",
              padding: "1.5mm",
              border: "1px solid #333",
              fontSize: "8.55pt",
              minHeight: "6mm",
              backgroundColor: "white",
            }}
          ></div>
        </div>
      </div>
      <div style={{ textAlign: "center", marginTop: "10mm" }}>
        <div
          style={{
            borderTop: "1px solid #000",
            paddingTop: "1.5mm",
            display: "inline-block",
            minWidth: "40mm",
          }}
        >
          <span style={{ fontSize: "7.6pt", fontWeight: "500" }}>
            Signature of Window Collector/Caretaker
          </span>
        </div>
      </div>
    </div>

    {/* Verification Section */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "4mm",
        marginBottom: "5mm",
      }}
    >
      <div
        style={{
          textAlign: "center",
          border: "1px solid #000",
          padding: "10mm 3mm 3mm 3mm",
          backgroundColor: "#fff",
        }}
      >
        <div style={{ borderTop: "1px solid #000", paddingTop: "1.5mm" }}>
          <span style={{ fontSize: "7.6pt", fontWeight: "500" }}>
            Verified by Hostel Warden
          </span>
        </div>
      </div>
      <div
        style={{
          textAlign: "center",
          border: "1px solid #000",
          padding: "10mm 3mm 3mm 3mm",
          backgroundColor: "#fff",
        }}
      >
        <div style={{ width: "100%" }}>
          <div style={{ borderTop: "1px solid #000", paddingTop: "1.5mm" }}>
            <span style={{ fontSize: "7.6pt", fontWeight: "500" }}>
              Signature of Chief Warden/In-Charge, Student Affairs
            </span>
          </div>
        </div>
      </div>
    </div>

    {/* Hostel Copy */}
    <div
      style={{
        border: "1px solid #000",
        padding: "3mm",
        backgroundColor: "#fff",
      }}
    >
      <h4
        style={{
          fontWeight: "bold",
          textAlign: "center",
          marginBottom: "2mm",
          fontSize: "9.5pt",
          textDecoration: "underline",
        }}
      >
        HOSTEL COPY
      </h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2mm",
          fontSize: "9.5pt",
          marginBottom: "3mm",
        }}
      >
        <div>
          <strong>Student Name:</strong> {formData.studentName}
        </div>
        <div>
          <strong>Roll No / Enrollment No.:</strong> {formData.rollNo} / {formData.enrollmentNo}
        </div>
        <div>
          <strong>Hostel Name:</strong> {formData.hostel}
        </div>
        <div>
          <strong>Room No.:</strong> {formData.room}
        </div>
        {/* NEW: Room Type, right after Room No. to mirror the allocation box above */}
        <div>
          <strong>Room Type:</strong> {formData.capacity}
        </div>
        <div>
          <strong>Programme Name:</strong> {formData.programme}
        </div>
        <div>
          <strong>Card No.:</strong> ________________
        </div>
        <div>
          <strong>Mess No.:</strong> ________________
        </div>
        <div>
          <strong>Sr. No.:</strong> ________________
        </div>
      </div>
      <div style={{ textAlign: "center", marginTop: "10mm" }}>
        <div
          style={{
            borderTop: "1px solid #000",
            paddingTop: "1.5mm",
            display: "inline-block",
            minWidth: "50mm",
          }}
        >
          <span style={{ fontSize: "7.6pt", fontWeight: "500" }}>
            Signature of Chief Warden/In-Charge, Student Affairs
          </span>
        </div>
      </div>
    </div>
  </div>
);
const renderFormPage2 = () => (
  <div
    id="form-page-2"
    className="bg-white mx-auto"
    style={{
      width: "210mm",
      height: "307mm", // Decreased by 10mm (1cm)
      padding: "10mm 9mm", // Slightly tuned padding to balance space
      fontFamily: "Arial, sans-serif",
      fontSize: "8.5pt", 
      lineHeight: "1.32",
      color: "#000",
      overflow: "hidden",
      boxSizing: "border-box",
    }}
  >
    {/* Header */}
    <div
      style={{
        textAlign: "center",
        marginBottom: "4.5mm", 
        borderBottom: "1.5px solid #000",
        paddingBottom: "2mm",
      }}
    >
      <h1
        style={{
          fontSize: "12.5pt", 
          fontWeight: "bold",
          margin: "0",
          color: "#1e40af",
          textTransform: "uppercase",
        }}
      >
        HOSTEL ACCOMMODATION FORM-B
      </h1>
      <h2 style={{ fontSize: "10.5pt", fontWeight: "600", margin: "1.5mm 0" }}>
        GAUTAM BUDDHA UNIVERSITY, GAUTAM BUDH NAGAR, GREATER NOIDA
      </h2>
      <h3
        style={{
          fontSize: "10pt", 
          fontWeight: "600",
          margin: "0.5mm 0",
          color: "#1e40af",
        }}
      >
        (2026-27)
      </h3>
    </div>

    {/* Student Information */}
    <div
      style={{
        marginBottom: "4.5mm", 
        border: "1px solid #1e40af",
        padding: "3mm 3.5mm", 
        backgroundColor: "#f0f4ff",
      }}
    >
      <h4
        style={{
          fontWeight: "bold",
          fontSize: "10pt", 
          marginBottom: "2mm",
          color: "#1e40af",
          textAlign: "center",
          textDecoration: "underline",
        }}
      >
        STUDENT INFORMATION
      </h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "3.5mm",
        }}
      >
        {/* Row 1: Name and Roll No */}
        <div>
          <label style={{ fontWeight: "bold", fontSize: "8.5pt", display: "block", marginBottom: "1.5mm" }}>
            Name:
          </label>
          <div style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "8.5pt", backgroundColor: "#f5f5f5", fontWeight: "500", minHeight: "7.5mm", display: "flex", alignItems: "center", boxSizing: "border-box" }}>
            {formData.studentName}
          </div>
        </div>
        <div>
          <label style={{ fontWeight: "bold", fontSize: "8.5pt", display: "block", marginBottom: "1.5mm" }}>
            Roll No / Enrollment No:
          </label>
          <div style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "8.5pt", backgroundColor: "#f5f5f5", fontWeight: "500", minHeight: "7.5mm", display: "flex", alignItems: "center", boxSizing: "border-box" }}>
            {formData.rollNo} / {formData.enrollmentNo}
          </div>
        </div>

        {/* Row 2: Date of Birth, Gender, Blood Group */}
        <div style={{ gridColumn: "span 2" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "3.5mm" }}>
            <div>
              <label style={{ fontWeight: "bold", fontSize: "8.5pt", display: "block", marginBottom: "1.5mm" }}>
                Date of Birth:
              </label>
              <div style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "8.5pt", backgroundColor: "#f5f5f5", fontWeight: "500", minHeight: "7.5mm", display: "flex", alignItems: "center", boxSizing: "border-box" }}>
                {formData.dateOfBirth}
              </div>
            </div>
            <div>
              <label style={{ fontWeight: "bold", fontSize: "8.5pt", display: "block", marginBottom: "1.5mm" }}>
                Gender:
              </label>
              <div style={{ display: "flex", gap: "4.5mm", height: "7.5mm", alignItems: "center" }}>
                <label style={{ fontSize: "8.5pt", display: "flex", alignItems: "center", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="gender"
                    value="M"
                    checked={formData.gender === "M"}
                    onChange={(e) => handleInputChange("gender", e.target.value)}
                    style={{ marginRight: "1.5mm" }}
                  />
                  M
                </label>
                <label style={{ fontSize: "8.5pt", display: "flex", alignItems: "center", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="gender"
                    value="F"
                    checked={formData.gender === "F"}
                    onChange={(e) => handleInputChange("gender", e.target.value)}
                    style={{ marginRight: "1.5mm" }}
                  />
                  F
                </label>
              </div>
            </div>
            <div>
              <label style={{ fontWeight: "bold", fontSize: "8.5pt", display: "block", marginBottom: "1.5mm" }}>
                Blood Group:
              </label>
              <input
                type="text"
                style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "8.5pt", backgroundColor: "white", minHeight: "7.5mm", boxSizing: "border-box" }}
                value={formData.bloodGroup || ""}
                onChange={(e) => handleInputChange("bloodGroup", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Row 3: Mobile No and Email */}
        <div style={{ gridColumn: "span 2" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3.5mm" }}>
            <div>
              <label style={{ fontWeight: "bold", fontSize: "8.5pt", display: "block", marginBottom: "1.5mm" }}>
                Mobile No.:
              </label>
              <div style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "8.5pt", backgroundColor: "#f5f5f5", fontWeight: "500", minHeight: "7.5mm", display: "flex", alignItems: "center", boxSizing: "border-box" }}>
                {formData.studentContact}
              </div>
            </div>
            <div>
              <label style={{ fontWeight: "bold", fontSize: "8.5pt", display: "block", marginBottom: "1.5mm" }}>
                Email ID:
              </label>
              <div style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "8.5pt", backgroundColor: "#f5f5f5", fontWeight: "500", minHeight: "7.5mm", display: "flex", alignItems: "center", overflow: "hidden", textOverflow: "ellipsis", boxSizing: "border-box" }}>
                {formData.email}
              </div>
            </div>
          </div>
        </div>

        {/* Row 4: Address */}
        <div style={{ gridColumn: "span 2" }}>
          <label style={{ fontWeight: "bold", fontSize: "8.5pt", display: "block", marginBottom: "1.5mm" }}>
            Address:
          </label>
          <div style={{ width: "100%", padding: "2mm", border: "1px solid #333", fontSize: "8.5pt", backgroundColor: "#f5f5f5", fontWeight: "500", minHeight: "10mm", display: "flex", alignItems: "flex-start", boxSizing: "border-box" }}>
            {formData.address}
          </div>
        </div>
      </div>
    </div>

    {/* Parents' Information Container Split */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4.5mm", marginBottom: "4.5mm" }}>

      {/* Father Information Card */}
      <div style={{ border: "1px solid #16a34a", padding: "3.5mm 4mm", backgroundColor: "#f0fdf4" }}>
        <h4 style={{ fontWeight: "bold", fontSize: "10.5pt", marginBottom: "2.5mm", color: "#16a34a", textAlign: "center", textDecoration: "underline" }}>
          FATHER INFORMATION
        </h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 32mm", gap: "3mm" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "3mm" }}>
            <div>
              <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Name:</label>
              <div style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", backgroundColor: "#f5f5f5", fontWeight: "500", minHeight: "7.5mm", display: "flex", alignItems: "center", boxSizing: "border-box" }}>
                {formData.fatherName}
              </div>
            </div>
            <div>
              <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Occupation:</label>
              <input type="text" style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", backgroundColor: "white", minHeight: "7.5mm", boxSizing: "border-box" }} value={formData.fatherOccupation || ""} onChange={(e) => handleInputChange("fatherOccupation", e.target.value)} />
            </div>
          </div>

          {/* Passport Photo Box (Father) */}
          <div style={{ border: "1.5px dashed #16a34a", padding: "1mm", backgroundColor: "#f9fafb", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: "7pt", fontWeight: "bold", color: "#16a34a", lineHeight: "1" }}>Father's Photo</span>
            <span style={{ fontSize: "6.5pt", color: "#666", marginBottom: "1mm" }}>35 x 45 mm</span>
            <div style={{ width: "24mm", height: "30mm", border: "1px solid #ccc", backgroundColor: "white" }} />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2.5mm" }}>
              <div>
                <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Mobile No.:</label>
                <div style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", backgroundColor: "#f5f5f5", fontWeight: "500", minHeight: "7.5mm", display: "flex", alignItems: "center", boxSizing: "border-box" }}>
                  {formData.fatherContact}
                </div>
              </div>
              <div>
                <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Aadhaar No.:</label>
            <input type="text" style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", backgroundColor: "white", minHeight: "7.5mm", boxSizing: "border-box" }} value={formData.fatherAadhaar || ""} onChange={(e) => handleInputChange("fatherAadhaar", e.target.value)} placeholder="XXXX XXXX XXXX" maxLength={14} />
              </div>
            </div>
          </div>

          <div style={{ gridColumn: "span 2" }}>
           <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Email ID:</label>
                <input type="email" style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", backgroundColor: "white", minHeight: "7.5mm", boxSizing: "border-box" }} value={formData.fatherEmail || ""} onChange={(e) => handleInputChange("fatherEmail", e.target.value)} />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Address:</label>
            <textarea style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", minHeight: "11mm", backgroundColor: "white", boxSizing: "border-box", resize: "none" }} value={formData.parentAddress || ""} onChange={(e) => handleInputChange("parentAddress", e.target.value)} />
          </div>
        </div>
      </div>

      {/* Mother Information Card */}
      <div style={{ border: "1px solid #d97706", padding: "3.5mm 4mm", backgroundColor: "#fffbeb" }}>
        <h4 style={{ fontWeight: "bold", fontSize: "10.5pt", marginBottom: "2.5mm", color: "#d97706", textAlign: "center", textDecoration: "underline" }}>
          MOTHER INFORMATION
        </h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 32mm", gap: "3mm" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "3mm" }}>
            <div>
              <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Name:</label>
              <input type="text" style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", backgroundColor: "white", minHeight: "7.5mm", boxSizing: "border-box" }} value={formData.motherName || ""} onChange={(e) => handleInputChange("motherName", e.target.value)} placeholder="Mother's Name" />
            </div>
            <div>
              <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Occupation:</label>
              <input type="text" style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", backgroundColor: "white", minHeight: "7.5mm", boxSizing: "border-box" }} value={formData.motherOccupation || ""} onChange={(e) => handleInputChange("motherOccupation", e.target.value)} placeholder="Occupation" />
            </div>
          </div>

          {/* Passport Photo Box (Mother) */}
          <div style={{ border: "1.5px dashed #d97706", padding: "1mm", backgroundColor: "#f9fafb", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: "7pt", fontWeight: "bold", color: "#d97706", lineHeight: "1" }}>Mother's Photo</span>
            <span style={{ fontSize: "6.5pt", color: "#666", marginBottom: "1mm" }}>35 x 45 mm</span>
            <div style={{ width: "24mm", height: "30mm", border: "1px solid #ccc", backgroundColor: "white" }} />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2.5mm" }}>
              <div>
                <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Mobile No.:</label>
                <input type="text" style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", backgroundColor: "white", minHeight: "7.5mm", boxSizing: "border-box" }} value={formData.motherContact || ""} onChange={(e) => handleInputChange("motherContact", e.target.value)} placeholder="Mobile No." />
              </div>
              <div>
             <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Aadhaar No.:</label>
            <input type="text" style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", backgroundColor: "white", minHeight: "7.5mm", boxSizing: "border-box" }} value={formData.motherAadhaar || ""} onChange={(e) => handleInputChange("motherAadhaar", e.target.value)} placeholder="XXXX XXXX XXXX" maxLength={14} />
              </div>
            </div>
          </div>

          <div style={{ gridColumn: "span 2" }}>
                            <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Email ID:</label>
                <input type="email" style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", backgroundColor: "white", minHeight: "7.5mm", boxSizing: "border-box" }} value={formData.motherEmail || ""} onChange={(e) => handleInputChange("motherEmail", e.target.value)} placeholder="Email" />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>Address:</label>
            <textarea style={{ width: "100%", padding: "1.5mm", border: "1px solid #333", fontSize: "9pt", minHeight: "11mm", backgroundColor: "white", boxSizing: "border-box", resize: "none" }} value={formData.motherAddress || ""} onChange={(e) => handleInputChange("motherAddress", e.target.value)} placeholder="Address" />
          </div>
        </div>
      </div>
    </div>

    {/* Emergency Contact / Local Guardian Section */}
    <div
      style={{
        border: "1px solid #16a34a",
        padding: "3.5mm 4mm",
        backgroundColor: "#f0fdf4",
      }}
    >
      <h4
        style={{
          fontWeight: "bold",
          fontSize: "10.5pt",
          marginBottom: "2.5mm",
          color: "#16a34a",
          textAlign: "center",
          textDecoration: "underline",
        }}
      >
        EMERGENCY CONTACT / LOCAL GUARDIAN INFORMATION
      </h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 32mm",
          gap: "3mm",
        }}
      >
        {/* Input Fields Wrapper */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2.5mm" }}>
          <div>
            <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>
              Name:
            </label>
            <input
              type="text"
              style={{
                width: "100%",
                padding: "1.5mm",
                border: "1px solid #333",
                fontSize: "9pt",
                backgroundColor: "white",
                minHeight: "7.5mm",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div>
            <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>
              Mobile Number:
            </label>
            <input
              type="text"
              style={{
                width: "100%",
                padding: "1.5mm",
                border: "1px solid #333",
                fontSize: "9pt",
                backgroundColor: "white",
                minHeight: "7.5mm",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Aadhaar No Field */}
          <div style={{ gridColumn: "span 2" }}>
            <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>
              Aadhaar No.:
            </label>
            <input
              type="text"
              style={{
                width: "100%",
                padding: "1.5mm",
                border: "1px solid #333",
                fontSize: "9pt",
                backgroundColor: "white",
                minHeight: "7.5mm",
                boxSizing: "border-box",
              }}
              value={formData.guardianAadhaar || ""}
              onChange={(e) => handleInputChange("guardianAadhaar", e.target.value)}
              placeholder="XXXX XXXX XXXX"
              maxLength={14}
            />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={{ fontWeight: "bold", fontSize: "9pt", display: "block", marginBottom: "1.5mm" }}>
              Email-ID & Address:
            </label>
            <textarea
              style={{
                width: "100%",
                padding: "1.5mm",
                border: "1px solid #333",
                fontSize: "9pt",
                minHeight: "10mm",
                backgroundColor: "white",
                boxSizing: "border-box",
                resize: "none",
              }}
              value={formData.parentAddress || ""}
              onChange={(e) =>
                handleInputChange("parentAddress", e.target.value)
              }
            />
          </div>
        </div>

        {/* Passport Photo Box (Local Guardian) */}
        <div style={{ border: "1.5px dashed #16a34a", padding: "1mm", backgroundColor: "#f9fafb", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: "7pt", fontWeight: "bold", color: "#16a34a", lineHeight: "1" }}>Guardian Photo</span>
          <span style={{ fontSize: "6.5pt", color: "#666", marginBottom: "1mm" }}>35 x 45 mm</span>
          <div style={{ width: "24mm", height: "30mm", border: "1px solid #ccc", backgroundColor: "white" }} />
        </div>
      </div>
    </div>
  </div>
);

  // Continue with similar improved styling for other pages...

const renderFormPage5 = () => (
  <div
    id="form-page-3"
    className="bg-white mx-auto"
    style={{
      width: "210mm",
      height: "297mm",
      padding: "5mm 10mm",
      fontFamily: "Arial, sans-serif",
      fontSize: "9pt",
      lineHeight: "1.2",
      color: "#000",
      overflow: "hidden",
      boxSizing: "border-box",
    }}
  >
    {/* Header */}
    <div
      style={{
        textAlign: "center",
        marginBottom: "5mm",
        borderBottom: "2px solid #000",
        paddingBottom: "4mm",
      }}
    >
      <h1 style={{ fontSize: "13pt", fontWeight: "bold", margin: "0" }}>
        DECLARATIONS & ITEM CHECKLIST
      </h1>
      <h2 style={{ fontSize: "11pt", fontWeight: "600", margin: "2mm 0" }}>
        GAUTAM BUDDHA UNIVERSITY, GAUTAM BUDH NAGAR, GREATER NOIDA
      </h2>
      <h3 style={{ fontSize: "10pt", fontWeight: "600", margin: "1mm 0" }}>
        (2026-27)
      </h3>
    </div>

    {/* Declaration by Student */}
    <div
      style={{
        marginBottom: "3mm",
        border: "2px solid #1e40af",
        padding: "2mm 4mm 4mm 4mm",
        backgroundColor: "#f0f4ff",
      }}
    >
      <h4
        style={{
          fontWeight: "bold",
          fontSize: "13pt",
          marginBottom: "3mm",
          color: "#1e40af",
          textAlign: "center",
        }}
      >
        DECLARATION BY THE STUDENT
      </h4>
      <p
        style={{
          fontSize: "10pt",
          marginBottom: "4mm",
          lineHeight: "1.3",
          textAlign: "justify",
        }}
      >
        I hereby declare that all the information given above is true and
        correct to the best of my knowledge and belief. I have carefully read
        the Student Hostel Handbook and the rules and regulations of the
        hostel, and I promise to abide by them. I understand that any
        violation of the rules and regulations may result in warning, fine,
        suspension, or disciplinary action against me, including
        cancellation of my hostel accommodation.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "6mm",
        }}
      >
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "11pt",
              display: "block",
              marginBottom: "2.5mm",
            }}
          >
            Date:
          </label>
          <input
            type="date"
            style={{
              width: "100%",
              padding: "2mm",
              border: "1px solid #333",
              fontSize: "10pt",
              backgroundColor: "white",
              minHeight: "6mm",
            }}
            value={formData.studentDeclarationDate || ""}
            onChange={(e) =>
              handleInputChange("studentDeclarationDate", e.target.value)
            }
          />
        </div>
        <div
          style={{ textAlign: "center", display: "flex", alignItems: "end" }}
        >
          <div style={{ width: "100%" }}>
            <div style={{ borderTop: "1px solid #000", paddingTop: "2mm" }}>
              <span style={{ fontSize: "10pt", fontWeight: "500" }}>
                Student Signature*
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Declaration by Parent/Guardian */}
    <div
      style={{
        marginBottom: "3mm",
        border: "2px solid #16a34a",
        padding: "2mm 4mm 4mm 4mm",
        backgroundColor: "#f0fdf4",
      }}
    >
      <h4
        style={{
          fontWeight: "bold",
          fontSize: "13pt",
          marginBottom: "3mm",
          color: "#16a34a",
          textAlign: "center",
        }}
      >
        DECLARATION BY THE PARENT/GUARDIAN
      </h4>
      <p
        style={{
          fontSize: "10pt",
          marginBottom: "4mm",
          lineHeight: "1.3",
          textAlign: "justify",
        }}
      >
        I hereby declare that all the information given above is true and
        correct to the best of my knowledge and belief. I have carefully read
        the Student Hostel Handbook and the rules and regulations of the
        hostel, and I shall ensure that my ward abides by them. I understand
        that any violation of the rules and regulations may result in
        warning, fine, suspension, or disciplinary action against my ward,
        including cancellation of hostel accommodation.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "6mm",
        }}
      >
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "11pt",
              display: "block",
              marginBottom: "2.5mm",
            }}
          >
            Date:
          </label>
          <input
            type="date"
            style={{
              width: "100%",
              padding: "2mm",
              border: "1px solid #333",
              fontSize: "10pt",
              backgroundColor: "white",
              minHeight: "6mm",
            }}
            value={formData.parentDeclarationDate || ""}
            onChange={(e) =>
              handleInputChange("parentDeclarationDate", e.target.value)
            }
          />
        </div>
        <div
          style={{ textAlign: "center", display: "flex", alignItems: "end" }}
        >
          <div style={{ width: "100%" }}>
            <div style={{ borderTop: "1px solid #000", paddingTop: "2mm" }}>
              <span style={{ fontSize: "10pt", fontWeight: "500" }}>
                Parent/Guardian Signature*
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Checklist of Items */}
    <div
      style={{
        border: "2px solid #dc2626",
        padding: "2mm 4mm 4mm 4mm",
        backgroundColor: "#fef2f2",
      }}
    >
      <h4
        style={{
          fontWeight: "bold",
          fontSize: "13pt",
          marginBottom: "3mm",
          color: "#dc2626",
          textAlign: "center",
          textDecoration: "underline",
        }}
      >
        CHECKLIST OF ITEMS RECEIVED BY THE STUDENT
      </h4>
      <p
        style={{
          fontSize: "9pt",
          marginBottom: "3mm",
          fontStyle: "italic",
          textAlign: "center",
        }}
      >
        (To be filled by the Warden/Caretaker)
      </p>

      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          border: "1px solid #000",
          fontSize: "10pt",
          backgroundColor: "white",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#f5f5f5" }}>
            <th
              style={{
                border: "1px solid #000",
                padding: "2mm",
                textAlign: "left",
                fontWeight: "bold",
              }}
            >
              Item
            </th>
            <th
              style={{
                border: "1px solid #000",
                padding: "2mm",
                textAlign: "center",
                fontWeight: "bold",
              }}
            >
              Yes
            </th>
            <th
              style={{
                border: "1px solid #000",
                padding: "2mm",
                textAlign: "center",
                fontWeight: "bold",
              }}
            >
              No
            </th>
          </tr>
        </thead>
        <tbody>
          {[
            "1. Bed",
            "2. Book Case",
            "3. Fan",
            "4. CFL(if Applicable)",
            "5. Tubelight",
            "6. Study Table",
            "7. Chair",
            "8. Almirah",
            "9. Net Port",
            "10. Other (Specify)",
          ].map((item, index) => (
            <tr key={index}>
              <td style={{ border: "1px solid #000", padding: "2mm" }}>
                {item}
              </td>
              <td
                style={{
                  border: "1px solid #000",
                  padding: "2mm",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "4mm",
                    height: "4mm",
                    border: "1px solid #333",
                    margin: "0 auto",
                    backgroundColor: "white",
                  }}
                ></div>
              </td>
              <td
                style={{
                  border: "1px solid #000",
                  padding: "2mm",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "4mm",
                    height: "4mm",
                    border: "1px solid #333",
                    margin: "0 auto",
                    backgroundColor: "white",
                  }}
                ></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "4mm",
          marginTop: "4mm",
        }}
      >
        <div>
          <label
            style={{
              fontWeight: "bold",
              fontSize: "11pt",
              display: "block",
              marginBottom: "2.5mm",
            }}
          >
            Date:
          </label>
          <input
            type="date"
            style={{
              width: "100%",
              padding: "2mm",
              border: "1px solid #333",
              fontSize: "8pt",
              backgroundColor: "white",
              minHeight: "6mm",
            }}
          />
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              borderTop: "1px solid #000",
              paddingTop: "2mm 2mm 1mm",
              marginTop: "10mm",
            }}
          >
            <span style={{ fontSize: "10pt", fontWeight: "500" }}>
              Signature of Warden/Caretaker
            </span>
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              borderTop: "1px solid #000",
              paddingTop: "2mm 2mm 1mm",
              marginTop: "10mm",
            }}
          >
            <span style={{ fontSize: "10pt", fontWeight: "500" }}>
              Signature of Student
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);



  // Add similar improved versions for pages 4, 5, and 6...
  // const renderFormPage3 = () => (
  //   <div
  //     id="form-page-4"
  //     className="bg-white mx-auto"
  //     style={{
  //       width: "210mm",
  //       height: "297mm",
  //       padding: "15mm",
  //       fontFamily: "Arial, sans-serif",
  //       fontSize: "11pt",
  //       lineHeight: "1.3",
  //       color: "#000",
  //       overflow: "hidden",
  //     }}
  //   >
  //     <div
  //       style={{
  //         textAlign: "center",
  //         marginBottom: "8mm",
  //         borderBottom: "2px solid #000",
  //         paddingBottom: "6mm",
  //       }}
  //     >
  //       <h1
  //         style={{
  //           fontSize: "15pt",
  //           fontWeight: "bold",
  //           margin: "0",
  //           color: "#dc2626",
  //         }}
  //       >
  //         UNDERTAKING FOR ANTI-RAGGING
  //       </h1>
  //       <h2 style={{ fontSize: "13pt", fontWeight: "600", margin: "3mm 0" }}>
  //         GAUTAM BUDDHA UNIVERSITY, GAUTAM BUDH NAGAR, GREATER NOIDA
  //       </h2>
  //       <h3
  //         style={{
  //           fontSize: "12pt",
  //           fontWeight: "600",
  //           margin: "2mm 0",
  //           color: "#dc2626",
  //         }}
  //       >
  //         (2025-26)
  //       </h3>
  //       <p style={{ fontSize: "9pt", margin: "3mm 0", fontStyle: "italic" }}>
  //         Undertaking form for the Students as per the provisions of
  //         Anti-Ragging verdict by the Hon'ble Supreme Court
  //       </p>
  //     </div>

  //     <div
  //       style={{
  //         border: "2px solid #dc2626",
  //         padding: "6mm",
  //         backgroundColor: "#fef2f2",
  //       }}
  //     >
  //       <div
  //         style={{ marginBottom: "6mm", fontSize: "11pt", lineHeight: "1.4" }}
  //       >
  //         <span>I, Mr./Ms. </span>
  //         <span
  //           style={{
  //             fontWeight: "bold",
  //           }}
  //         >
  //           {formData.studentName}
  //         </span>
  //         <span> of Program: </span>
  //         <span
  //           style={{
  //             fontWeight: "bold",
  //           }}
  //         >
  //           {formData.programme}
  //         </span>
  //         <span>, Roll No.: </span>
  //         <span
  //           style={{
  //             fontWeight: "bold",
  //           }}
  //         >
  //           {formData.rollNo}
  //         </span>
  //         <span> Student of undertake on this </span>
  //         <span
  //           style={{
  //             fontWeight: "bold",
  //           }}
  //         >
  //           {new Date().toLocaleDateString()}
  //         </span>
  //         <span>
  //           , the following with respect to above subject and office Order No:
  //           Dir-
  //         </span>
  //         <span
  //           style={{
  //             fontWeight: "bold",
  //           }}
  //         >
  //           __________
  //         </span>
  //       </div>

  //       <div
  //         style={{
  //           fontSize: "11pt",
  //           lineHeight: "1.4",
  //           textAlign: "justify",
  //           marginBottom: "8mm",
  //         }}
  //       >
  //         <p style={{ marginBottom: "4mm" }}>
  //           <strong>1)</strong> That I have read and understood the directives
  //           of the Hon'ble Supreme Court of India on Anti-ragging and the
  //           measures proposed to taken in the above references. (Available at
  //           http://www.peopelsgroup.in)
  //         </p>
  //         <p style={{ marginBottom: "4mm" }}>
  //           <strong>2)</strong> That I understand the meaning of ragging and
  //           know that the ragging in any form is a punishable offence and the
  //           same is banned by the Court of Law.
  //         </p>
  //         <p style={{ marginBottom: "4mm" }}>
  //           <strong>3)</strong> That I have not been found or charged for my
  //           involvement in any kind of ragging in the past. However, I undertake
  //           to face disciplinary action/ legal proceedings including expulsion
  //           from the Institute if the above statement is found to be untrue or
  //           the facts are concealed, at any stage in future.
  //         </p>
  //         <p style={{ marginBottom: "4mm" }}>
  //           <strong>4)</strong> That I shall not resort to ragging in any form
  //           at any place and shall abide by the rules/laws prescribed by the
  //           Courts, Govt. of India and the Institute authorities for the purpose
  //           from time to time.
  //         </p>
  //       </div>

  //       <div
  //         style={{
  //           display: "grid",
  //           gridTemplateColumns: "1fr 1fr",
  //           gap: "12mm",
  //           marginTop: "12mm",
  //         }}
  //       >
  //         <div style={{ textAlign: "center" }}>
  //           <div
  //             style={{
  //               borderTop: "2px solid #000",
  //               paddingTop: "3mm",
  //               marginTop: "10mm",
  //             }}
  //           >
  //             <span style={{ fontSize: "10pt", fontWeight: "500" }}>
  //               Signature of Student*
  //             </span>
  //           </div>
  //         </div>
  //         <div>
  //           <p
  //             style={{
  //               fontSize: "10pt",
  //               marginBottom: "15mm",
  //               textAlign: "center",
  //             }}
  //           >
  //             I hereby fully endorse the undertaking made by child /ward.
  //           </p>
  //           <div style={{ textAlign: "center" }}>
  //             <div style={{ borderTop: "2px solid #000", paddingTop: "3mm" }}>
  //               <span style={{ fontSize: "10pt", fontWeight: "500" }}>
  //                 Signature of Mother/Father or Guardian**
  //               </span>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   </div>
  // );



const renderFormPage3 = () => (
  <div
    id="form-page-5"
    className="bg-white mx-auto"
    style={{
      width: "210mm",
      height: "297mm",
      padding: "12mm",
      fontFamily: "Arial, sans-serif",
      fontSize: "9.5pt",
      lineHeight: "1.2",
      color: "#000",
      overflow: "hidden",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
    }}
  >
    {/* Header */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "4mm",
        borderBottom: "1.5px solid #000",
        paddingBottom: "3mm",
        flex: "0 0 auto",
      }}
    >
      <div style={{ marginRight: "4mm" }}>
        <img
          src="https://hostels.gbu.ac.in/images/logo/GBU%20logo.png"
          alt="GBU Logo"
          style={{
            width: "13mm",
            height: "13mm",
            objectFit: "contain",
          }}
          crossOrigin="anonymous"
        />
      </div>
      <div style={{ flex: 1, textAlign: "center" }}>
        <h1 style={{ fontSize: "12pt", fontWeight: "bold", margin: "0" }}>
          गौतम बुद्ध विश्वविद्यालय
        </h1>
        <h2 style={{ fontSize: "10.5pt", fontWeight: "600", margin: "1.5mm 0" }}>
          Gautam Buddha University
        </h2>
      </div>
    </div>

    {/* Title block */}
    <div style={{ textAlign: "center", marginBottom: "4mm", flex: "0 0 auto" }}>
      <h1
        style={{
          fontSize: "13pt",
          fontWeight: "bold",
          margin: "0",
          color: "#16a34a",
        }}
      >
        UNDERTAKING FOR ABIDING GENERAL RULES AND REGULATIONS
      </h1>
      <h2 style={{ fontSize: "10.5pt", fontWeight: "600", margin: "2mm 0" }}>
        GAUTAM BUDDHA UNIVERSITY, GAUTAM BUDH NAGAR, GREATER NOIDA
      </h2>
      <h3
        style={{
          fontSize: "10pt",
          fontWeight: "600",
          margin: "1mm 0",
          color: "#16a34a",
        }}
      >
        (2026-27)
      </h3>
    </div>

    {/* Rules box - grows to fill remaining space, but can't exceed it */}
    <div
      style={{
        flex: "1",
        border: "1px solid #16a34a",
        padding: "4.5mm 6mm",
        backgroundColor: "#f0fdf4",
        marginBottom: "4mm",
        display: "flex",
        flexDirection: "column",
        
      }}
    >
      <p
        style={{
          fontSize: "9.5pt",
          marginBottom: "2mm",
          textAlign: "justify",
          fontWeight: "500",
          flex: "0 0 auto",
        }}
      >
        The following are the key highlights of the rules and regulations
        mentioned in the Student Hostel Handbook. You must take note of
        these rules.
      </p>

      <div
        style={{
          fontSize: "8.5pt",
          lineHeight: "1.30",
          textAlign: "justify",
          columnCount: 2,
          columnGap: "7mm",
        }}
      >
        {[
          "Every hostel resident must carry his/her University Identity Card at all times.",
          "Hostel residents are not permitted to use powered vehicles within university campus. Hostel residents are encouraged to use bicycles to move within campus.",
          "Hostel timings must be strictly followed as per hostel policy.",
          "Violation of hostel timings or repeated late entry may result in fine/disciplinary action and cancellation of hostel allotment as per university policy",
          "Attendance will be taken twice daily-once after the closing of the main hostel gate and again after the closing of the inner gate of the respective hostel. Students must be present during attendance.",
          "Filling leave form and rebate form is mandatory before leaving the hostel to go home. It is the responsibility of the hostel resident to inform their hostel leaving plan to their parents before leaving the hostel. Hostel/University administration will not be responsible if the student leaves the hostel without informing their parents and hostel administration.",
          "Hostel residents will never indulge in any act of indiscipline in the Hostel room, corridors and verandahs that would cause disturbance to the fellow residents.",
          "Hostel residents are responsible for their conduct and actions both inside and outside the hostel within the University campus.",
          "Appropriate and decent casual attire must be worn in the dining hall and other common areas of the hostel.",
          "Smoking, consumption of alcohol and use or possession of any intoxicating substances are strictly prohibited within the hostel premises. Any such activity will be treated as a serious violation of hostel rules and will be a punishable offence.",
          "Any damage to hostel property will be considered a punishable offence, and the cost of repair may be recovered from the student(s) found accountable for the damage. Recovery of damage done to hostel property will be collectively imposed upon all residents if enquiry is unable to identify specific individual(s) involved.",
          "Any health condition (psychological and physiological) that requires extra medical care is the responsibility of hostel residents and their parents.",
          "Every hostel resident has to pay all fees (academic and hostel fees) as per university schedule and take no dues from the account section to collect admit cards for the end semester exam.",
          "Keeping or using any prohibited / unauthorized extra bedding, furniture, or other hostelproperty in a hostel room shall be treated as a violation of the Hostel Rules. Any resident found in possession of such unauthorized items shall be liable to disciplinary action as per university policies.",
          "Submitting an Anti Ragging form through the link mentioned is mandatory at the time of hostel allotment. (https://www.antiragging.in/affidavit_registration_disclaimer.html)",
          "Parents/Guardians can only visit their ward’s hostel but cannot stay with them in the hostel rooms.",
          "Hostel residents/Students are not allowed to move around residential areas (faculty and staff) within university campus.",
          "It is mandatory to vacate the hostel during the summer vacation, failure of which may lead to fine or disciplinary action as university policy.",
          "Submission of all the forms and undertaking given on hostel portal, along with hostel fee payment slip, two passport-size photographs, and a copy of the Aadhar Card (student/parents/guardians), is mandatory at the time of hostel possession. Failure of which will lead to hostel allotment cancellation.",
        ].map((rule, index) => (
          <p
            key={index}
            style={{
              marginBottom: "1.8mm",
              paddingLeft: "2.5mm",
              breakInside: "avoid",
            }}
          >
            <strong>{index + 1}.</strong> {rule}
          </p>
        ))}
      </div>

      <p
        style={{
          fontSize: "8.8pt",
          marginTop: "auto",
          paddingTop: "3mm",
          textAlign: "justify",
          flex: "0 0 auto",
        }}
      >
        Hostel residents should read the Student Hostel Handbook thoroughly. Violation of any hostel ruleor provision of the Student Hostel Handbook may result in warning, fine, suspension, or cancellation of hostel accommodation.
      </p>
    </div>

    {/* Declaration + signatures - fixed height, anchored at bottom */}
    <div
      style={{
        border: "1px solid #1e40af",
        padding: "4.5mm 6mm",
        backgroundColor: "#f0f4ff",
        flex: "0 0 auto",
      }}
    >
      <p
        style={{
          fontSize: "9.5pt",
          marginBottom: "3mm",
          textAlign: "justify",
          fontWeight: "500",
        }}
      >
        We agree to abide by the rules and regulations outlined in the
        Student Hostel Handbook (available on the University website). We,
        the hostel resident and the parent/guardian, understand that failure
        to comply with these rules and all guidelines of the Student Hostel
        Handbook may result in disciplinary action, penalties, or any other
        consequences as deemed appropriate by the University authorities on
        the hostel resident.
      </p>

      <p style={{ fontSize: "9.5pt", marginBottom: "2mm" }}>
        Student's Name: <strong>{formData.studentName}</strong>{" "}
        (Roll No / Enrollment No.: <strong>{formData.rollNo} / {formData.enrollmentNo}</strong>)
      </p>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "8mm",
        }}
      >
        <div
          style={{
            borderTop: "1px solid #000",
            paddingTop: "1.5mm",
            minWidth: "62mm",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "9.5pt", fontWeight: "500" }}>
            Signature of Student*
          </span>
        </div>
        <div
          style={{
            borderTop: "1px solid #000",
            paddingTop: "1.5mm",
            minWidth: "62mm",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "9.5pt", fontWeight: "500" }}>
            Signature of Parent/Guardian**
          </span>
        </div>
      </div>
    </div>
  </div>
);


const renderFormPage4 = () => (
  <div
    id="form-page-4"
    className="bg-white mx-auto"
    style={{
      width: "210mm",
      height: "297mm",
      padding: "6mm 12mm 6mm 12mm",
      fontFamily: "Arial, sans-serif",
      fontSize: "9pt",
      lineHeight: "1.3",
      color: "#000",
      overflow: "hidden",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    {/* Upper Section */}
    <div>
      {/* Page Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "2.5mm",
          borderBottom: "1.5px solid #000",
          paddingBottom: "2mm",
        }}
      >
        <div style={{ marginRight: "4mm", display: "flex", alignItems: "center" }}>
          {!logoError ? (
            <img
              src="https://hostels.gbu.ac.in/images/logo/GBU%20logo.png"
              alt="GBU Logo"
              style={{ width: "13mm", height: "13mm", objectFit: "contain" }}
              onError={() => setLogoError(true)}
            />
          ) : (
            <div
              style={{
                width: "13mm",
                height: "13mm",
                backgroundColor: "#1e40af",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
                fontSize: "7.5pt",
              }}
            >
              GBU
            </div>
          )}
        </div>
        <div style={{ flex: 1, textAlign: "center" }}>
          <h1 style={{ fontSize: "11pt", fontWeight: "bold", margin: "0" }}>
            गौतम बुद्ध विश्वविद्यालय
          </h1>
          <h2 style={{ fontSize: "9.5pt", fontWeight: "600", margin: "0.5mm 0" }}>
            GAUTAM BUDDHA UNIVERSITY, GREATER NOIDA
          </h2>
          <div>
            <span
              style={{
                color: "#1e40af",
                fontSize: "9pt",
                fontWeight: "bold",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Medical & Fitness Undertaking (2026-27)
            </span>
          </div>
        </div>
      </div>

      {/* Physical Details & Contacts Box */}
      <div
        style={{
          border: "1px solid #1e40af",
          padding: "4mm 5mm",
          backgroundColor: "#f0f4ff",
          marginBottom: "3mm",
          borderRadius: "4px",
          boxSizing: "border-box",
        }}
      >
        <h4
          style={{
            fontWeight: "bold",
            fontSize: "9pt",
            color: "#1e40af",
            textAlign: "center",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            margin: "0 0 3mm 0",
          }}
        >
          Physical Details & Contacts
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: "6mm",
            rowGap: "4mm",
          }}
        >
          {[
            { label: "Height*:", key: "height" },
            { label: "Weight*:", key: "weight" },
            { label: "Mother's Name:", key: "motherName" },
            { label: "Mother's Phone*:", key: "motherPhone" },
            { label: "Father's Name:", key: "fatherName" },
            { label: "Father's Phone:", key: "fatherContact" },
            { label: "Other Contact Name*:", key: "otherContactName" },
            { label: "Phone*:", key: "otherContactPhone" },
          ].map((field) => (
            <div
              key={field.key}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <label
                style={{
                  fontWeight: "bold",
                  fontSize: "8.5pt",
                  marginBottom: "1.5mm",
                  color: "#000",
                  display: "block",
                  lineHeight: "1",
                }}
              >
                {field.label}
              </label>
              <input
                type="text"
                style={{
                  width: "100%",
                  height: "7.5mm",
                  padding: "0 2.5mm",
                  border: "1px solid #333",
                  fontSize: "10pt",
                  fontWeight: "500",
                  backgroundColor: "white",
                  boxSizing: "border-box",
                  outline: "none",
                  margin: "0",
                  lineHeight: "1.2",
                  display: "block",
                }}
                value={formData[field.key] || ""}
                onChange={(e) => handleInputChange(field.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Medical Questions Box */}
      <div
        style={{
          border: "1px solid #dc2626",
          padding: "2.5mm 3.5mm",
          backgroundColor: "#fef2f2",
          marginBottom: "2.5mm",
          borderRadius: "4px",
          boxSizing: "border-box",
        }}
      >
        <h4
          style={{
            fontWeight: "bold",
            fontSize: "8.5pt",
            color: "#dc2626",
            textAlign: "center",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            margin: "0 0 1.5mm 0",
          }}
        >
          Medical Information
        </h4>
        {[
          { key: "physicalDisabilities", label: "Physical Disabilities*" },
          { key: "respiratoryProblems", label: "Respiratory Problem/Asthma*" },
          { key: "visionHearingProblems", label: "Vision/Hearing Problem*" },
          { key: "otherDiseases", label: "Other diseases not listed above*" },
        ].map((item, index) => (
          <div
            key={index}
            style={{
              marginBottom: "1.5mm",
              border: "1px solid #e5e7eb",
              padding: "1.5mm 2mm",
              backgroundColor: "white",
              borderRadius: "2px",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "2mm",
                fontSize: "8.5pt",
                flexWrap: "nowrap",
              }}
            >
              <span style={{ fontWeight: "bold", minWidth: "45mm" }}>
                {item.label}:
              </span>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="radio"
                  name={item.key}
                  value="yes"
                  checked={formData[item.key] === "yes"}
                  onChange={(e) => handleInputChange(item.key, e.target.value)}
                  style={{ marginRight: "1mm" }}
                />
                Yes
              </label>
              <label style={{ display: "flex", alignItems: "center", marginRight: "2mm", cursor: "pointer" }}>
                <input
                  type="radio"
                  name={item.key}
                  value="no"
                  checked={formData[item.key] === "no"}
                  onChange={(e) => handleInputChange(item.key, e.target.value)}
                  style={{ marginRight: "1mm" }}
                />
                No
              </label>
              <span style={{ fontSize: "8.5pt", marginRight: "1mm", fontWeight: "500" }}>
                If Yes:
              </span>
              <input
                type="text"
                style={{
                  flex: 1,
                  height: "6.5mm",
                  padding: "0 1.5mm",
                  border: "1px solid #333",
                  fontSize: "10pt",
                  fontWeight: "500",
                  backgroundColor: "white",
                  boxSizing: "border-box",
                  outline: "none",
                  lineHeight: "1.2",
                }}
                value={formData[`${item.key}Details`] || ""}
                onChange={(e) => handleInputChange(`${item.key}Details`, e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Declaration Section Box */}
      <div
        style={{
          border: "1px solid #333",
          padding: "3mm 4mm",
          backgroundColor: "#fafafa",
          marginBottom: "2.5mm",
          borderRadius: "4px",
          fontSize: "9pt",
          lineHeight: "1.3",
          boxSizing: "border-box",
        }}
      >
        <p style={{ margin: "0 0 1.5mm 0", textAlign: "justify" }}>
          We, Mr./Ms. <span style={{ fontWeight: "600", textDecoration: "underline", fontSize: "10pt" }}>{formData.studentName || "________________"}</span> (Candidate) and Mr./Mrs./Ms.{" "}
          <span style={{ fontWeight: "600", textDecoration: "underline", fontSize: "10pt" }}>{formData.fatherName || "________________"}</span> (Parent/Guardian), hereby declare and undertake that the candidate is medically fit to pursue the academic programme and reside in the University Hostel.
        </p>
        <p style={{ margin: "0 0 1.5mm 0", fontWeight: "bold" }}>
          We certify that the candidate is not suffering from any contagious or infectious disease.
        </p>
        <div style={{ display: "flex", gap: "2mm", marginBottom: "1.5mm", alignItems: "baseline" }}>
          <span style={{ whiteSpace: "nowrap", fontSize: "8.5pt" }}>
            Does the student suffer from any chronic/contagious disease? If yes, provide details:
          </span>
          <input 
            type="text" 
            style={{ 
              flex: 1, 
              border: "none", 
              borderBottom: "1px dotted #000", 
              outline: "none", 
              fontSize: "10pt", 
              fontWeight: "500",
              backgroundColor: "transparent", 
              padding: 0 
            }}
            value={formData.chronicDiseaseDetails || ""}
            onChange={(e) => handleInputChange("chronicDiseaseDetails", e.target.value)}
          />
        </div>
        <div style={{ display: "flex", gap: "2mm", marginBottom: "1.5mm", alignItems: "baseline" }}>
          <span style={{ whiteSpace: "nowrap", fontSize: "8.5pt" }}>
            Is the student susceptible to any allergy? If yes, specify:
          </span>
          <input 
            type="text" 
            style={{ 
              flex: 1, 
              border: "none", 
              borderBottom: "1px dotted #000", 
              outline: "none", 
              fontSize: "10pt", 
              fontWeight: "500",
              backgroundColor: "transparent", 
              padding: 0 
            }}
            value={formData.allergyDetails || ""}
            onChange={(e) => handleInputChange("allergyDetails", e.target.value)}
          />
        </div>
        <p style={{ margin: "0 0 1mm 0", textAlign: "justify" }}>
          We understand that concealment or misrepresentation of any medical information may result in disciplinary action, including cancellation of admission and/or hostel allotment, as per university rules.
        </p>
        <p style={{ margin: "0", textAlign: "justify" }}>
          We declare that the information furnished above is true and correct to the best of our knowledge.
        </p>
      </div>
    </div>

    {/* Bottom Section: Signatures */}
    <div style={{ padding: "0", marginTop: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "6mm",
          marginBottom: "2.5mm",
        }}
      >
        {/* Student Box */}
        <div style={{ border: "1px solid #000", padding: "2.5mm", backgroundColor: "#fff" }}>
          <div style={{ marginBottom: "1.5mm" }}>
            <span style={{ fontWeight: "bold", fontSize: "8.5pt" }}>Student’s Name:</span>
            <div
              style={{
                borderBottom: "1px dotted #000",
                marginTop: "1mm",
                fontSize: "10.5pt",
                fontWeight: "600",
                minHeight: "4mm",
              }}
            >
              {formData.studentName}
            </div>
          </div>
          <div style={{ marginTop: "8mm" }}>
            <div
              style={{
                borderTop: "1px dashed #000",
                paddingTop: "1mm",
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: "8.5pt", fontWeight: "bold" }}>Signature of Student</span>
            </div>
          </div>
        </div>

        {/* Parent Box */}
        <div style={{ border: "1px solid #000", padding: "2.5mm", backgroundColor: "#fff" }}>
          <div style={{ marginBottom: "1.5mm" }}>
            <span style={{ fontWeight: "bold", fontSize: "8.5pt" }}>Parent’s/Guardian’s Name:</span>
            <div
              style={{
                borderBottom: "1px dotted #000",
                marginTop: "1mm",
                fontSize: "10.5pt",
                fontWeight: "600",
                minHeight: "4mm",
              }}
            >
              {formData.fatherName}
            </div>
          </div>
          <div style={{ marginTop: "8mm" }}>
            <div
              style={{
                borderTop: "1px dashed #000",
                paddingTop: "1mm",
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: "8.5pt", fontWeight: "bold" }}>
                Signature of Parent/Guardian
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Date & Location Footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "9pt",
          borderTop: "1px solid #000",
          paddingTop: "1.5mm",
        }}
      >
        <div>
          <strong>Place:</strong> Greater Noida
        </div>
        <div>
          <strong>Date:</strong> {new Date().toLocaleDateString("en-IN")}
        </div>
      </div>
    </div>
  </div>
);


const renderFormPage6 = () => (
  <div
    id="form-page-6"
    className="bg-white mx-auto"
    style={{
      width: "210mm",
      height: "280mm",
      padding: "15mm 15mm 12mm 15mm",
      fontFamily: "Arial, sans-serif",
      fontSize: "10.5pt",
      lineHeight: "1.6",
      color: "#000",
      overflow: "hidden",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    {/* Upper Section (Header + Clauses) */}
    <div>
      {/* Page Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "8mm",
          borderBottom: "1.5px solid #000",
          paddingBottom: "4mm",
        }}
      >
        <div style={{ marginRight: "5mm", display: "flex", alignItems: "center" }}>
          {!logoError ? (
            <img
              src="https://hostels.gbu.ac.in/images/logo/GBU%20logo.png"
              alt="GBU Logo"
              style={{
                width: "16mm",
                height: "16mm",
                objectFit: "contain",
              }}
              onError={() => setLogoError(true)}
            />
          ) : (
            <div
              style={{
                width: "16mm",
                height: "16mm",
                backgroundColor: "#1e40af",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
                fontSize: "9pt",
              }}
            >
              GBU
            </div>
          )}
        </div>
        <div style={{ flex: 1, textAlign: "center" }}>
          <h1 style={{ fontSize: "13pt", fontWeight: "bold", margin: "0" }}>
            गौतम बुद्ध विश्वविद्यालय
          </h1>
          <h2 style={{ fontSize: "11.5pt", fontWeight: "600", margin: "1.5mm 0" }}>
            GAUTAM BUDDHA UNIVERSITY, GREATER NOIDA
          </h2>
          <div style={{ marginTop: "2mm" }}>
            <span
              style={{
                color: "#1e40af",
                fontSize: "11pt",
                fontWeight: "bold",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              UNDERTAKING FROM STUDENT AND PARENT/GUARDIAN
            </span>
          </div>
        </div>
      </div>

      {/* Main Undertaking Clauses */}
      <div style={{ marginTop: "8mm", textAlign: "justify", padding: "0 1mm" }}>
        <div style={{ display: "flex", marginBottom: "6mm" }}>
          <span style={{ fontWeight: "bold", marginRight: "3mm", minWidth: "6mm", fontSize: "11pt" }}>
            1.
          </span>
          <p style={{ margin: 0, fontSize: "10.5pt" }}>
            It is the responsibility of the student to inform their parents about their hostel leaving
            plans and take permission from them before leaving the hostel. Filling the leave form in online
            mode and also submitting the hard copy in the hostel office is mandatory to take permission for
            staying out of the hostel even for a single day, failing which may lead to hostel allotment
            cancellation. Parents can access the university hostel portal using their ward’s login credentials
            and keep updated about their ward’s leave and vacation departures. It is the sole responsibility
            of parents / guardians to regularly log in, monitor, and independently verify their ward's travel
            and leave logs. The university expressly disclaims any liability, tracking responsibility, or
            accountability for a student’s movements, actions, or safety once they exit the campus gates.
          </p>
        </div>

        <div style={{ display: "flex", marginBottom: "8mm" }}>
          <span style={{ fontWeight: "bold", marginRight: "3mm", minWidth: "6mm", fontSize: "11pt" }}>
            2.
          </span>
          <p style={{ margin: 0, fontSize: "10.5pt" }}>
            In addition to other restricted items, we will not keep / provide powered vehicles (two-wheeler,
            four-wheeler etc.).
          </p>
        </div>

        {/* Declaration Statement Box */}
        <div
          style={{
            border: "1px solid #333",
            padding: "6mm",
            backgroundColor: "#fafafa",
            marginTop: "6mm",
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: 0, fontWeight: "500", textAlign: "justify", fontSize: "10.5pt", lineHeight: "1.6" }}>
            We assure that all the rules and regulations of the University / hostel will be strictly abided. If
            he/she is found guilty for violation of any rule, the university has the right to expel from the
            hostel / university as per university policy.
          </p>
        </div>
      </div>
    </div>

    {/* Bottom Section: Signatures & Details */}
    <div style={{ padding: "0 1mm", marginTop: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12mm",
          marginBottom: "10mm",
        }}
      >
        {/* Student Box */}
        <div style={{ border: "1px solid #000", padding: "5mm", backgroundColor: "#fff" }}>
          <div style={{ marginBottom: "4mm" }}>
            <span style={{ fontWeight: "bold", fontSize: "10pt" }}>Student’s Name:</span>
            <div
              style={{
                borderBottom: "1px dotted #000",
                marginTop: "2mm",
                fontSize: "11pt",
                fontWeight: "600",
                minHeight: "6mm",
              }}
            >
              {formData.studentName}
            </div>
          </div>
          <div style={{ marginTop: "24mm" }}>
            <div
              style={{
                borderTop: "1px dashed #000",
                paddingTop: "2mm",
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: "9.5pt", fontWeight: "bold" }}>Signature of Student</span>
            </div>
          </div>
        </div>

        {/* Parent Box */}
        <div style={{ border: "1px solid #000", padding: "5mm", backgroundColor: "#fff" }}>
          <div style={{ marginBottom: "4mm" }}>
            <span style={{ fontWeight: "bold", fontSize: "10pt" }}>Parent’s/Guardian’s Name:</span>
            <div
              style={{
                borderBottom: "1px dotted #000",
                marginTop: "2mm",
                fontSize: "11pt",
                fontWeight: "600",
                minHeight: "6mm",
              }}
            >
              {formData.fatherName}
            </div>
          </div>
          <div style={{ marginTop: "24mm" }}>
            <div
              style={{
                borderTop: "1px dashed #000",
                paddingTop: "2mm",
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: "9.5pt", fontWeight: "bold" }}>
                Signature of Parent/Guardian
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Date & Location Footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "10pt",
          borderTop: "1px solid #000",
          paddingTop: "4mm",
        }}
      >
        <div>
          <strong>Place:</strong> Greater Noida
        </div>
        <div>
          <strong>Date:</strong> {new Date().toLocaleDateString("en-IN")}
        </div>
      </div>
    </div>
  </div>
);


  return (
    <div className="flex min-h-screen bg-gray-50">
      {notification && (
        <NotificationPopup
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        onLogout={onLogout}
      />
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col md:ml-64">
        <Topbar
          setSidebarOpen={setSidebarOpen}
          enrollment={studentData?.student?.enrollment_id}
        />

        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading && <p className="text-gray-500">Loading...</p>}
          {error && <p className="text-red-600">{error}</p>}

          {!loading && !error && studentData && (
            <>
              {/* Header Card */}
              <div className="bg-white rounded-xl shadow flex flex-col md:flex-row items-center p-6">
                <img
                  src={getImageUrl() || "/placeholder.svg"}
                  alt="Student Photograph"
                  className="w-24 h-24 object-cover rounded-full border-2 border-blue-200 shadow mb-4 md:mb-0 md:mr-6"
                  onError={(e) => {
                    e.target.src = "/placeholder-avatar.png";
                  }}
                />
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-blue-700 mb-1">
                    Hostel Allotment Forms -{" "}
                    {studentData.student.name || "Student"}
                  </h2>
                  <p className="text-gray-600">
                    Enrollment No:{" "}
                    <span className="font-mono">
                      {studentData.student.enrollment_id}
                    </span>
                  </p>
                  <p className="text-gray-600">
                    Programme: {studentData.profile?.ProgrammeName || "N/A"}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Fill missing information and download each page as PDF for
                    submission.
                  </p>
                </div>
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => downloadPDF(currentPage)}
                    disabled={downloading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    {downloading
                      ? "Downloading..."
                      : `Download Page ${currentPage}`}
                  </button>

                  <button
                    onClick={downloadAllPDFs}
                    disabled={downloading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    {downloading ? "Downloading All..." : "Download All Pages"}
                  </button>
                </div>
              </div>

              {/* Form Progress */}
              <div className="bg-white rounded-xl shadow p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">
                    Form Pages
                  </h3>
                  <div className="flex items-center space-x-1 text-sm text-gray-600">
                    <span>
                      Page {currentPage} of {totalPages}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(currentPage / totalPages) * 100}%` }}
                  ></div>
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>Form A</span>
                  <span>Form B</span>
                  <span>Rules</span>
                  <span>Medical</span>
                  <span>Undertaking</span>
                  <span>Declarations</span>
                </div>
              </div>

              {/* Fees Summary */}
              {feesData && feesData.success && feesData.sessions.length > 0 && (
                <div className="bg-white rounded-xl shadow p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-700">
                      Fees Summary
                    </h3>
                    <button
                      onClick={loadFees}
                      disabled={feesLoading}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      <RefreshCw className="w-4 h-4" />
                      {feesLoading ? "Refreshing..." : "Sync Latest"}
                    </button>
                  </div>
                  <div className="space-y-4">
                    {feesData.sessions.map((session, idx) => (
                      <div
                        key={idx}
                        className="border rounded-lg p-4 bg-gray-50"
                      >
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="text-md font-semibold text-blue-700">
                            {session.session_name}
                          </h4>
                          <span
                            className={`text-sm px-3 py-1 rounded-full ${session.allowed_to_allot_hostel
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                              }`}
                          >
                            {session.allowed_to_allot_hostel
                              ? "Eligible for Hostel"
                              : "Not Eligible"}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">
                              Academic Fees:
                            </span>
                            <div className="font-medium">
                              ₹{session.academic_fees_paid} / ₹
                              {session.academic_fees_total}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-600">Hostel Fees:</span>
                            <div className="font-medium">
                              ₹{session.hostel_fees_paid} / ₹
                              {session.hostel_fees_total}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-600">Mess Fees:</span>
                            <div className="font-medium">
                              ₹{session.mess_fees_paid} / ₹
                              {session.mess_fees_total}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Form Content */}
              <div className="bg-gray-100 p-4 rounded-xl overflow-x-auto">
                {currentPage === 1 && renderFormPage1()}
                {currentPage === 2 && renderFormPage2()}
                {currentPage === 3 && renderFormPage3()}
                {/* {currentPage === 4 && renderFormPage4()} */}
                {currentPage === 4 && renderFormPage4()}
                {currentPage === 5 && renderFormPage6()}
                {currentPage === 6 && renderFormPage5()}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center bg-white rounded-xl shadow p-4">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <div className="flex items-center space-x-2">
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                  >
                    <Home className="w-4 h-4" />
                    Back to Dashboard
                  </Link>
                </div>

                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </main>
        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
}
